import { wrapApiCall } from '@/lib/axios'
import { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { FacilityBookingMembership } from '../facility/model'
import { TrainingService } from '../masterData/model'
import { PageInfo, PaginationResponse } from '../model'
import {
  ClientHistoryData,
  CoachTrainingHistory,
  CreateServiceGroupBody,
  CreditBalance,
  Service,
  ServiceGroupDetail,
  ServiceGroupListResponse,
  TrainerSchedule,
  TrainerScheduleBody,
  TrainerScheduleBooking,
  TrainerScheduleParam,
  TrainerServiceBody,
  UpdateServiceGroupBody,
} from './model'

export const TrainerService = (client: AxiosInstance) => {
  return {
    async getTrainerSchedules(
      id: number,
      params: TrainerScheduleParam
    ): Promise<TrainerSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/trainer/${id}/schedules`, { params })
      )
      return data?.map((item) => deserialize(item, TrainerSchedule))
    },
    async getCurrentCredit(id: number): Promise<CreditBalance> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/credits/balance/${id}`)
      )
      return deserialize(data, CreditBalance)
    },
    async bookAppointment(appointmentBody: TrainerScheduleBody): Promise<TrainerScheduleBooking> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/trainer/pt-booking', appointmentBody)
      )
      return deserialize(data, TrainerScheduleBooking)
    },
    async findMembershipForPtBooking(
      trainerId: number,
      params: { userId: number; serviceId: number; date?: string }
    ): Promise<FacilityBookingMembership> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/trainer/${trainerId}/find-membership`, { params })
      )
      return deserialize(data, FacilityBookingMembership)
    },
    async getTrainingService(): Promise<TrainingService[]> {
      const { data } = await wrapApiCall(() => client.get<Record<string, unknown>[]>(`/services`))
      return data?.map((item) => deserialize(item, TrainingService))
    },
    async addTrainerService(body: TrainerServiceBody): Promise<Service> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/services', body)
      )
      return deserialize(data, Service)
    },
    async editTrainerService(id: number, body: TrainerServiceBody): Promise<Service> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/services/${id}`, body)
      )
      return deserialize(data, Service)
    },
    async deleteTrainerService(id: number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/services/${id}`)
      )
      return !!data?.success
    },
    async updatePtBookingStatus(
      id: number,
      status: 'BOOKING_SUCCESS' | 'CANCELLED' | 'COMPLETED' | 'NOT_PARTICIPATED',
      note?: string,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      // The mutation is branch-scoped on the BE while the member pt-history
      // list is cross-branch — send the booking's own branch for this request
      // so rows from other branches don't 404 (BE still authorizes the user
      // against that branch).
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(
          `/trainer/pt-booking/status`,
          { id, status, note },
          config
        )
      )
      return !!data?.success
    },
    async cancelPtBookingSchedule(
      id: number,
      userId: number,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/trainer/pt-booking/${id}`, {
          data: {
            userId: userId,
          },
          ...(opts?.branchIdOverride != null
            ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
            : {}),
        })
      )
      return !!data?.success
    },
    async cancelUnavailableSchedule(id: number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/trainer/unavailable/${id}`)
      )
      return !!data?.success
    },
    async getCoachSwimmingTrainingHistory(
      trainerId: number,
      page?: number,
      pageSize?: number,
      statuses?: string,
      search?: string
    ): Promise<PaginationResponse<CoachTrainingHistory>> {
      const statusList = statuses
        ? statuses
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined

      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/bookings/swimming`,
          {
            params: {
              trainerId,
              page,
              pageSize,
              statuses: statusList,
              search,
            },
            paramsSerializer: () =>
              new URLSearchParams({
                trainerId: String(trainerId),
                page: String(page ?? ''),
                statuses: String(statuses ?? ''),
                pageSize: String(pageSize ?? ''),
                query: search ?? '',
              }).toString(),
          }
        )
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, CoachTrainingHistory)),
      }
    },
    async getCoachClientHistory(
      params?: Record<string, unknown>
    ): Promise<PaginationResponse<ClientHistoryData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          `/trainer/pt/training-history`,
          { params }
        )
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, ClientHistoryData)),
      }
    },
    async getServiceGroups(
      page: number = 1,
      pageSize: number = 10
    ): Promise<ServiceGroupListResponse> {
      const { data } = await wrapApiCall(() =>
        client.get<ServiceGroupListResponse>(`/service-groups`, { params: { page, pageSize } })
      )
      return data
    },
    async getServiceGroup(id: number): Promise<ServiceGroupDetail> {
      const { data } = await wrapApiCall(() =>
        client.get<ServiceGroupDetail>(`/service-groups/${id}`)
      )
      return data
    },
    async createServiceGroup(body: CreateServiceGroupBody): Promise<ServiceGroupDetail> {
      const { data } = await wrapApiCall(() =>
        client.post<ServiceGroupDetail>(`/service-groups`, body)
      )
      return data
    },
    async updateServiceGroup(
      id: number,
      body: UpdateServiceGroupBody
    ): Promise<ServiceGroupDetail> {
      const { data } = await wrapApiCall(() =>
        client.patch<ServiceGroupDetail>(`/service-groups/${id}`, body)
      )
      return data
    },
    async deleteServiceGroup(id: number): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success: boolean }>(`/service-groups/${id}`)
      )
      return data
    },
  }
}
